import { normalizedReviews } from '../data/reviews';

const ORIGIN = 'https://br.lk';

// Real cross-platform review aggregate (Facebook + Google Maps + Trustpilot) —
// same dataset the testimonial UI renders, so schema and visible ratings agree.
const RATINGS = normalizedReviews.map((r) => r.rating).filter(Number.isFinite);
export const AGGREGATE_RATING = RATINGS.length
  ? {
      "@type": "AggregateRating",
      "ratingValue": Number((RATINGS.reduce((a, b) => a + b, 0) / RATINGS.length).toFixed(1)),
      "ratingCount": RATINGS.length,
      "bestRating": 5,
      "worstRating": 1,
    }
  : undefined;

export function breadcrumbSchema(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": crumbs.map((crumb, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": crumb.name,
      // Trailing-slash form to match canonical URLs (Cloudflare Pages 308s the
      // no-slash form). Root stays "/"; paths with a file extension are left as-is.
      "item": `${ORIGIN}${crumb.path === '/' || /\.[a-z0-9]+$/i.test(crumb.path) ? crumb.path : crumb.path.replace(/\/?$/, '/')}`
    }))
  };
}

export function serviceSchema({ name, description, url, price, currency = "USD", areaServed, serviceType, audience }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": name,
    "description": description,
    "url": `${ORIGIN}${url}`,
    "provider": {
      "@type": "Organization",
      "@id": `${ORIGIN}/#organization`,
      "name": "BR.LK",
      "url": ORIGIN
    }
  };
  if (serviceType) schema.serviceType = serviceType;
  if (Array.isArray(areaServed) && areaServed.length) {
    schema.areaServed = areaServed.map((a) =>
      typeof a === 'string' ? { "@type": "Country", "name": a } : a
    );
  }
  if (audience) {
    schema.audience = {
      "@type": "BusinessAudience",
      "audienceType": audience
    };
  }
  if (price != null) {
    schema.offers = {
      "@type": "Offer",
      "price": String(price),
      "priceCurrency": currency,
      "url": `${ORIGIN}${url}`,
      "availability": "https://schema.org/InStock"
    };
  }
  return schema;
}

export function blogPostingSchema({ title, description, url, image, datePublished, dateModified, author, authorUrl, wordCount, articleSection }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": description,
    "url": `${ORIGIN}${url}`,
    "image": image && /^https?:\/\//.test(image) ? image : image ? `${ORIGIN}${image}` : undefined,
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "inLanguage": "en",
    "author": {
      "@type": "Person",
      "name": author || "BR.LK Team",
      "url": authorUrl || `${ORIGIN}/about`
    },
    "publisher": {
      "@type": "Organization",
      "@id": `${ORIGIN}/#organization`,
      "name": "BR.LK",
      "logo": {
        "@type": "ImageObject",
        "url": `${ORIGIN}/android-chrome-512x512.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${ORIGIN}${url}`
    }
  };
  if (wordCount) schema.wordCount = wordCount;
  if (articleSection) schema.articleSection = articleSection;
  return schema;
}

export function webAppSchema({ name, description, url, category = "BusinessApplication" }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": name,
    "description": description,
    "url": `${ORIGIN}${url}`,
    "applicationCategory": category,
    "operatingSystem": "All",
    "browserRequirements": "Requires JavaScript",
    "aggregateRating": AGGREGATE_RATING,
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "provider": {
      "@type": "Organization",
      "name": "BR.LK",
      "url": ORIGIN
    }
  };
}

export function itemListSchema({ name, description, url, items }) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": name,
    "description": description,
    "url": `${ORIGIN}${url}`,
    "numberOfItems": items.length,
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "url": `${ORIGIN}${item.path}`
    }))
  };
}

export function personSchema({ name, jobTitle, url, image, sameAs = [], description, knowsAbout = [], worksForOrgId = `${ORIGIN}/#organization` }) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": url ? `${url}#person` : undefined,
    "name": name,
    ...(jobTitle ? { "jobTitle": jobTitle } : {}),
    ...(url ? { "url": url } : {}),
    ...(image ? { "image": image } : {}),
    ...(description ? { "description": description } : {}),
    ...(knowsAbout.length ? { "knowsAbout": knowsAbout } : {}),
    ...(sameAs.length ? { "sameAs": sameAs } : {}),
    "worksFor": {
      "@type": "Organization",
      "@id": worksForOrgId,
      "name": "BR.LK"
    }
  };
}

// NOTE: the org rating and review-list schema helpers were removed intentionally.
// Self-serving reviews/ratings of your own Organization (itemReviewed = BR.LK)
// violate Google's structured-data guidelines and risk a manual action.

export function offerCatalogSchema({ name, description, url, region, plans }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": name,
    "description": description,
    "url": `${ORIGIN}${url}`,
    "areaServed": region,
    "provider": {
      "@type": "Organization",
      "@id": `${ORIGIN}/#organization`,
      "name": "BR.LK",
      "url": ORIGIN
    },
    "offers": plans.map((p) => ({
      "@type": "Offer",
      "name": `${name} — ${p.name}`,
      "description": p.description,
      "price": String(p.price),
      "priceCurrency": p.currency || "USD",
      "url": `${ORIGIN}${url}`,
      "availability": "https://schema.org/InStock",
      "category": p.name,
      "seller": {
        "@type": "Organization",
        "@id": `${ORIGIN}/#organization`,
        "name": "BR.LK"
      }
    }))
  };
}

export function jobPostingSchema({ title, description, url, employmentType, datePosted, validThrough, identifier, country = "LK" }) {
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": title,
    "description": description,
    "url": `${ORIGIN}${url}`,
    "employmentType": employmentType,
    ...(identifier ? { "identifier": { "@type": "PropertyValue", "name": "BR.LK", "value": identifier } } : {}),
    // datePosted is required for Google for Jobs eligibility; validThrough stops
    // Google auto-expiring the listing ~30 days after crawl.
    ...(datePosted ? { "datePosted": datePosted } : {}),
    ...(validThrough ? { "validThrough": validThrough } : {}),
    "hiringOrganization": {
      "@type": "Organization",
      "name": "BR.LK",
      "sameAs": ORIGIN,
      "@id": `${ORIGIN}/#organization`,
      "logo": `${ORIGIN}/android-chrome-512x512.png`
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": country
      }
    },
    "applicantLocationRequirements": {
      "@type": "Country",
      "name": "Sri Lanka"
    }
  };
}

export function faqPageSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map(item => ({
      "@type": "Question",
      "name": item.question || item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer || item.a
      }
    }))
  };
}
